import { Deserializer, makeQueryStringSerializer } from 'core/lib/serializer';
import { stringify } from 'query-string';

import apiFetch from './apiFetch';
import { API_URL } from './constants';
import * as Types from './definitions';
import { makeValidPhoneNumber } from './validators';

interface CustomerSearchParams {
    query?: string;
    maxResults?: number;
    page?: number;
}

const programApiPath = (clientId: string, programId: string) =>
    `${API_URL}/api/v1/client/${clientId}/program/${programId}`;

const customerApiPath = (clientId: string, programId: string) => `${programApiPath(clientId, programId)}/Customer`;

const defaultRequest: Partial<Types.CustomerSearchRequest> = { page: 1 };
const serializer = makeQueryStringSerializer<Types.CustomerSearchRequest>(defaultRequest, {
    page: Deserializer.Int,
    sortDescending: Deserializer.Bool,
});

export default class Customer {
    static serializeRequest = serializer.serialize;

    static deserializeRequest = serializer.deserialize;

    static prepareForRequest(customer: Types.Customer): Types.Customer {
        const preparedCustomer: Types.Customer = {
            ...customer,
            phoneNumber: makeValidPhoneNumber(customer.phoneNumber),
            preferredPhoneNumber: makeValidPhoneNumber(customer.preferredPhoneNumber),
        };
        return preparedCustomer;
    }

    static async search(
        clientId: string,
        programId: string,
        params: CustomerSearchParams = {},
    ): Promise<Types.Customer[]> {
        const response = await apiFetch(`${customerApiPath(clientId, programId)}?${stringify(params)}`);
        const json = await response.json();
        return json;
    }

    static async searchClientCustomers(
        clientId: string,
        params: CustomerSearchParams = {},
    ): Promise<Types.ClientCustomerSearchResultsResponse> {
        const response = await apiFetch(
            `${API_URL}/api/v1/client/${clientId}/Program/CustomersByProgram?${stringify(params)}`,
        );
        const clientCustomerSearchResponse: Types.ClientCustomerSearchResultsResponse = await response.json();
        return clientCustomerSearchResponse;
    }

    static async searchAll(
        clientId: string,
        programId: string,
        customerSearchRequest: Partial<Types.CustomerSearchRequest>,
    ): Promise<Types.SearchResultsResponse<Types.Customer>> {
        const query = Customer.serializeRequest(customerSearchRequest, true);
        const response = await apiFetch(`${customerApiPath(clientId, programId)}/all/?${query}`);
        const customers: Types.SearchResultsResponse<Types.Customer> = await response.json();
        return customers;
    }

    static async searchByAddress(
        clientId: string,
        programId: string,
        params: CustomerSearchParams = {},
    ): Promise<Types.Customer[]> {
        const response = await apiFetch(`${customerApiPath(clientId, programId)}/address?${stringify(params)}`);
        const json = await response.json();
        return json;
    }

    static async fetchSingle(clientId: string, programId: string, customerId: string): Promise<Types.Customer> {
        const response = await apiFetch(`${customerApiPath(clientId, programId)}/${customerId}`);
        const json = await response.json();
        return json;
    }

    static async create(
        clientId: string,
        programId: string,
        customer: Types.Customer,
        fromMailIn: boolean | undefined,
    ): Promise<Types.CustomerActionResult> {
        const { id, ...outboundCustomer } = Customer.prepareForRequest(customer);

        if (fromMailIn) {
            outboundCustomer.IsMailIn = fromMailIn;
        }

        try {
            const response = await apiFetch(customerApiPath(clientId, programId), {
                headers: [['Content-Type', 'application/json']],
                method: 'POST',
                body: JSON.stringify(outboundCustomer),
            });
            const newId: string = await response.json();
            return { success: true, customer: { ...outboundCustomer, id: newId } };
        } catch (e) {
            if (e && e.response.status === 400) {
                const errors = (await e.response.json()) as Types.ErrorSummary<Types.Customer>;
                return { success: false, errors };
            }
            throw e;
        }
    }

    static async update(
        clientId: string,
        programId: string,
        customer: Types.Customer,
        fromMailIn: boolean | undefined,
    ): Promise<Types.CustomerActionResult> {
        const outboundCustomer = Customer.prepareForRequest(customer);

        if (fromMailIn) {
            outboundCustomer.IsMailIn = fromMailIn;
        }

        try {
            await apiFetch(customerApiPath(clientId, programId), {
                headers: [['Content-Type', 'application/json']],
                method: 'PUT',
                body: JSON.stringify(outboundCustomer),
            });
            return { success: true, customer: outboundCustomer };
        } catch (e) {
            if (e && e.response.status === 400) {
                const errors = (await e.response.json()) as Types.ErrorSummary<Types.Customer>;
                return { success: false, errors };
            }
            throw e;
        }
    }

    static async resetPassword(clientId: string, programId: string, customerId: string): Promise<Response> {
        return await apiFetch(`${customerApiPath(clientId, programId)}/${customerId}/resetPassword`, {
            method: 'POST',
        });
    }

    static async fetchCallHistory(clientId: string, programId: string, customerId: string): Promise<Types.CallState[]> {
        const response = await apiFetch(`${programApiPath(clientId, programId)}/Call/${customerId}`);
        const json = await response.json();
        return json;
    }

    static async fetchUpdateHistory(
        clientId: string,
        programId: string,
        customerId: string,
    ): Promise<Types.CustomerHistory[]> {
        const response = await apiFetch(`${customerApiPath(clientId, programId)}/${customerId}/history`);
        const json = await response.json();
        return json;
    }

    static async fetchCustomAttributeValues(
        clientId: string,
        programId: string,
        customerAttributeId: number,
        isCustom: boolean,
    ) {
        try {
            const response = await apiFetch(
                `${customerApiPath(clientId, programId)}/${customerAttributeId}` +
                    `/uniqueCustomAttributeValues/${isCustom}`,
            );
            const customeAttributeValues: string[] = await response.json();
            return customeAttributeValues;
        } catch (e) {
            const errorStr: string[] = await e.response.json();
            return errorStr;
        }
    }

    static async saveCustomerSSN(
        clientId: string,
        programId: string,
        ssnRequest: Types.CustomerSSNRequest,
    ): Promise<Response> {
        return await apiFetch(`${customerApiPath(clientId, programId)}/ssn`, {
            headers: [['Content-Type', 'application/json']],
            method: 'PUT',
            body: JSON.stringify(ssnRequest),
        });
    }

    static async fetchCustomerPromotionLimitLedger(
        clientId: string,
        programId: string,
        customerId: string,
    ): Promise<Types.CustomerPromotionLimitLedger> {
        const response = await apiFetch(
            `${customerApiPath(clientId, programId)}/CustomerPromotionLimitLedger/${customerId}`,
        );
        const json = await response.json();
        return json;
    }
}
